import React, { useMemo } from "react";

import { useSelector } from "react-redux";
import { Field, FormSection } from "redux-form";
import {
	ReduxControls,
	ErrorBanner
} from "rhombus-react";

import AssetSelect from "social/AssetSelect";

import { getErrorMessagesByAdType } from "selectors/Social2";

import { prettifyMessages } from "social/utils";
import { CREATIVE_NAME_MAX_LENGTH } from "widgets/forms/validators/Creatives";
import { YOUTUBE_DISCOVERY_CONSTRAINTS } from "social/validators/youtube";

import { ASSET_TYPES, SOCIAL_AD_TYPES } from "utils/constants";
import { formatBytes } from "utils/common";

const {
	MAX_LENGTH_VIDEO_TITLE,
	MAX_LENGTH_HEADLINE,
	MAX_LENGTH_DESCRIPTION_1,
	MAX_LENGTH_DESCRIPTION_2,

	VIDEO_ACCEPT,
	MAX_VIDEO_BYTES,

	MAX_THUMBNAIL_BYTES,
	THUMBNAIL_ACCEPT,
} = YOUTUBE_DISCOVERY_CONSTRAINTS;

const YOUTUBE_DISCOVERY_FIELDS = {
	thumbnail: {
		name: "thumbnail",
		label: "Image Thumbnail",
		component: AssetSelect,
		requirements: [
			{
				name: "File Size",
				description: `${formatBytes(MAX_THUMBNAIL_BYTES)} Max`,
			},
			{
				name: "File Types",
				description: THUMBNAIL_ACCEPT,
			},
		],
		required: true,
		className: "full-width",
		assetType: ASSET_TYPES.IMAGE,
		accept: THUMBNAIL_ACCEPT,
	},
	video: {
		name: "video",
		label: "Video",
		component: AssetSelect,
		requirements: [
			{
				name: "File Size",
				description: `${formatBytes(MAX_VIDEO_BYTES)} Max`,
			},
			{
				name: "File Types",
				description: VIDEO_ACCEPT,
			},
		],
		required: true,
		className: "full-width",
		assetType: ASSET_TYPES.VIDEO,
		accept: VIDEO_ACCEPT,
	},
	name: {
		name: "name",
		label: "Creative Name / Ad Name",
		placeholder: "Enter a creative name",
		component: ReduxControls.Input,
		required: true,
		className: "full-width",
		tip: {
			direction: "right",
			description: "Will also be used as the DV360 Ad name",
		},
		charMax: CREATIVE_NAME_MAX_LENGTH,
		showCharCount: true,
		showPristineErrors: true,
	},
	videoTitle: {
		name: "videoTitle",
		label: "Video Title",
		placeholder: "Enter a video title",
		component: ReduxControls.Input,
		required: true,
		className: "full-width",
		charMax: MAX_LENGTH_VIDEO_TITLE,
		showCharCount: true,
	},
	headline: {
		name: "headline",
		label: "Headline",
		placeholder: "Enter a short headline",
		component: ReduxControls.Input,
		required: true,
		className: "full-width",
		charMax: MAX_LENGTH_HEADLINE,
		showCharCount: true,
		tip: {
			direction: "right",
			description: "May get truncated after 25 characters",
		},
	},
	description1: {
		name: "description1",
		label: "Description 1",
		placeholder: "Tell people what your creative is about",
		component: ReduxControls.Input,
		required: true,
		className: "full-width",
		charMax: MAX_LENGTH_DESCRIPTION_1,
		showCharCount: true,
	},
	description2: {
		name: "description2",
		label: "Description 2",
		placeholder: "Tell people what your creative is about",
		component: ReduxControls.Input,
		required: true,
		className: "full-width",
		charMax: MAX_LENGTH_DESCRIPTION_2,
		showCharCount: true,
	},
};

const adType = SOCIAL_AD_TYPES.YOUTUBE_DISCOVERY;

export default function YouTubeDiscovery(props) {
	const { navigationActions, libraryId, isViewOnly, showPristineErrors } = props;

	const errorMessages = useSelector(getErrorMessagesByAdType(adType, showPristineErrors));
	const prettyErrorMessages = useMemo(() => (
		prettifyMessages(errorMessages, YOUTUBE_DISCOVERY_FIELDS)
	), [errorMessages]);
	const hasErrors = prettyErrorMessages.length;

	let errorDisplay;
	if (hasErrors) {
		errorDisplay = (
			<ErrorBanner title="Sorry, please fix the errors below" messages={prettyErrorMessages} />
		);
	}

	const $fields = Object.values(YOUTUBE_DISCOVERY_FIELDS).map((fieldProps) => {
		const extraProps = {};

		if (fieldProps.name === "thumbnail" || fieldProps.name === "video") {
			extraProps.navigationActions = navigationActions;
			extraProps.libraryId = libraryId;
		}

		extraProps.disabled = isViewOnly;

		if (extraProps.disabled && fieldProps.placeholder) {
			// We set this on extraProps, because we don't want to mutate the fieldProps consts
			extraProps.placeholder = '';
		}

		extraProps.showPristineErrors = showPristineErrors;

		return	<Field key={fieldProps.name} {...fieldProps} {...extraProps} />;
	});

	return (
		<div className="form-container">
			<FormSection name={adType}>
				{errorDisplay}
				<div className="fields">
					{$fields}
				</div>
			</FormSection>
		</div>
	);
}

YouTubeDiscovery.displayName = "YouTubeDiscoveryForm";
